import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { calculateFlyoutPosition, DateLocalePipe, repositionFlyoutAfterRender } from '@modeso/aumico-lib-core-fe';
import { IAnnualReportDto, ISetupData, ReportType } from '@modeso/types__aumico-lib-annualreport-be';
import { ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IAccountingBasisDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { HamButtonComponent } from '../../../../../../aumico-components/src/lib';
import { Errors } from '../../../enums/errors.enum';
import { GeneralInformationMode } from '../../../enums/general-information-form-mode.enum';
import { IGeneralInformationV2Output } from '../../../interfaces/general-information-form-output.interface';
import { DatesCalculatorUtil } from '../../../utils/dates.util';

@UntilDestroy()
@Component({
    template: '', // Abstract component
})
export abstract class GeneralInformationBaseComponent implements OnInit {
    @Input() setupData: ISetupData | undefined;
    @Input() companies: ICompanyNameIdLegalFormDTO[] = [];
    @Input() accountingBases: INameId[] = [];
    @Input() arTemplates: INameId[] = [];
    @Input() currencies: INameId[] = [];
    @Input() reportTypes: INameId[] = [];
    @Input() reportLanguages: INameId[] = [];
    @Input() currentLanguage = 'de-ch';
    @Input() defaultLanguage = 'de-ch';
    @Input() isCpmmActive = false;
    @Input() previousAnnualReportOptions: Partial<IAnnualReportDto>[] = [];

    // Select report dropdowns
    balanceSheetReportOptions: INameId[] = [];
    incomeStatementReportOptions: INameId[] = [];

    // Disabled state control for dropdowns
    disableCompany = false;
    disableAccountingBasis = false;
    disableArTemplate = false;
    disableLanguage = false;
    disableReportType = false;
    disableCurrency = false;
    disablePreviousSettings = false;

    @Output() companySelected = new EventEmitter<ICompanyNameIdLegalFormDTO>();
    @Output() accountingBasisSelected = new EventEmitter<IAccountingBasisDTO>();
    @Output() arTemplateSelected = new EventEmitter<string>();
    @Output() autofillReportForBalanceSelected = new EventEmitter<string>();
    @Output() autofillReportForIncomeSelected = new EventEmitter<string>();
    @Output() formDataChanged = new EventEmitter<IGeneralInformationV2Output>();
    @Output() formValidChanged = new EventEmitter<boolean>();

    form: FormGroup;

    // Flyout control properties
    openedBalanceSheetDateFlyout = false;
    openedBalancePreviousDateFlyout = false;
    openedIncomeCurrentFromDateFlyout = false;
    openedIncomeCurrentToDateFlyout = false;
    openedIncomePreviousFromDateFlyout = false;
    openedIncomePreviousToDateFlyout = false;
    openDialogForAutomaticDueDateChange = false;
    flyoutPosition: {
        top: number;
        left: number;
        right: number;
        isAbove?: boolean;
    } = { top: 0, left: 0, right: 0, isAbove: false };

    errors = Errors;

    // Store previous year dates when toggling off
    protected storedPreviousYearDates: {
        balancePreviousYearDate: Date | null;
        incomePreviousYearFromDate: Date | null;
        incomePreviousYearToDate: Date | null;
    } | null = null;

    // Abstract properties
    abstract get mode(): GeneralInformationMode;

    // Common getters
    get isNewMode(): boolean {
        return this.mode === GeneralInformationMode.NEW;
    }
    get isAutofillMode(): boolean {
        return this.mode === GeneralInformationMode.AUTOFILL;
    }
    get isSavedMode(): boolean {
        return this.mode === GeneralInformationMode.SAVED;
    }
    get showArTemplateDropdown(): boolean {
        return this.form?.get('accountingBasis')?.value != null;
    }
    get showLanguageDropdown(): boolean {
        return this.form?.get('arTemplate')?.value != null;
    }
    /**
     * The only test for "is this an FX report" — never derive it from `displayCurrency`, which is
     * merely a required field on such a report and is empty until the user fills it in.
     */
    get isFxReport(): boolean {
        return this.setupData?.fxEnabled === true;
    }

    constructor(protected formBuilder: FormBuilder, protected dateLocalePipe: DateLocalePipe) {
        this.initializeForm();
    }

    ngOnInit(): void {
        this.setupFormSubscriptions();
        // Initialize form control states after form is set up
        this.updateFormControlStates();
    }

    protected initializeForm(): void {
        this.form = this.formBuilder.group({
            company: [null, Validators.required],
            accountingBasis: [null, Validators.required],
            arTemplate: [null, Validators.required],
            locale: [null, Validators.required],
            reportType: [null, Validators.required],
            currency: [null, Validators.required],
            displayCurrency: [null],
            balanceSheetDate: [null, Validators.required],
            balancePreviousYearDate: [null],
            incomeCurrentYearFromDate: [null, Validators.required],
            incomeCurrentYearToDate: [null, Validators.required],
            incomePreviousYearFromDate: [null],
            incomePreviousYearToDate: [null],
            hasPreviousData: [true],
            autofillFromPreviousForBalance: [false],
            autofillReportForBalance: [null],
            autofillFromPreviousForIncome: [false],
            autofillReportForIncome: [null],
        });

        // Initialize form control states after form creation
        this.updateFormControlStates();
    }

    protected setupFormSubscriptions(): void {
        // Form value changes
        this.form.valueChanges
            .pipe(
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.emitFormData();
            });

        // Form validity changes
        this.form.statusChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(() => {
            this.formValidChanged.emit(this.form.valid);
        });

        // Company selection changes
        this.form
            .get('company')
            ?.valueChanges.pipe(
                filter((company) => company != null),
                untilDestroyed(this),
            )
            .subscribe((company: ICompanyNameIdLegalFormDTO) => {
                this.onCompanyChange(company);
            });

        // Accounting basis selection changes
        this.form
            .get('accountingBasis')
            ?.valueChanges.pipe(
                filter((accountingBasis) => accountingBasis != null),
                untilDestroyed(this),
            )
            .subscribe((accountingBasis: IAccountingBasisDTO) => {
                this.onAccountingBasisChange(accountingBasis);
            });

        // AR Template selection changes
        this.form
            .get('arTemplate')
            ?.valueChanges.pipe(
                filter((arTemplate) => arTemplate != null),
                untilDestroyed(this),
            )
            .subscribe((arTemplate: INameId) => {
                this.onArTemplateChange(arTemplate.id);
            });

        // Report Type changes
        this.form
            .get('reportType')
            ?.valueChanges.pipe(
                filter((reportType) => reportType != null),
                untilDestroyed(this),
            )
            .subscribe((reportType: INameId) => {
                this.onReportTypeChange(reportType);
            });

        // No previous data changes
        this.form
            .get('hasPreviousData')
            ?.valueChanges.pipe(untilDestroyed(this))
            .subscribe((hasPreviousData: boolean) => {
                this.onHasPreviousDataChange(hasPreviousData);
            });

        this.form.get('autofillReportForBalance')?.valueChanges.pipe(filter(autofillReportForBalance => autofillReportForBalance != null), untilDestroyed(this)).subscribe((autofillReportForBalance) => {
            this.onAutofillReportForBalanceChange(autofillReportForBalance.id);
        });

        this.form.get('autofillReportForIncome')?.valueChanges.pipe(filter(autofillReportForIncome => autofillReportForIncome != null), untilDestroyed(this)).subscribe((autofillReportForIncome) => {
            this.onAutofillReportForIncomeChange(autofillReportForIncome.id);
        });
    }

    protected onCompanyChange(company: ICompanyNameIdLegalFormDTO): void {
        // Reset dependent fields
        this.form.patchValue({
            accountingBasis: null,
            arTemplate: null,
            locale: null,
        });
        this.arTemplates = [];
        this.reportLanguages = [];

        this.companySelected.emit(company);
    }

    protected onAccountingBasisChange(accountingBasis: IAccountingBasisDTO): void {
        // Reset dependent fields
        this.form.patchValue({
            arTemplate: null,
            locale: null,
        });
        this.reportLanguages = [];

        this.accountingBasisSelected.emit(accountingBasis);
    }

    protected onArTemplateChange(arTemplateId: string): void {
        // Reset locale selection
        this.form.patchValue({ locale: null });

        this.arTemplateSelected.emit(arTemplateId);
    }

    protected onReportTypeChange(reportType: INameId): void {
        const sheetDates = DatesCalculatorUtil.calculateFinancialStatementDates(
            this.setupData.balanceSheetDate,
            reportType.id as ReportType,
        );

        this.form.patchValue({
            balanceSheetDate: sheetDates.balanceCurrentYearDate,
            balancePreviousYearDate: sheetDates.balancePreviousYearDate,
            incomeCurrentYearFromDate: sheetDates.incomeCurrentYearFromDate,
            incomeCurrentYearToDate: sheetDates.incomeCurrentYearToDate,
            incomePreviousYearFromDate: sheetDates.incomePreviousYearFromDate,
            incomePreviousYearToDate: sheetDates.incomePreviousYearToDate,
        });
    }

    protected onHasPreviousDataChange(hasPreviousData: boolean): void {
        if (!hasPreviousData) {
            // Store current previous year dates before clearing them
            this.storedPreviousYearDates = {
                balancePreviousYearDate: this.form.get('balancePreviousYearDate')?.value || null,
                incomePreviousYearFromDate: this.form.get('incomePreviousYearFromDate')?.value || null,
                incomePreviousYearToDate: this.form.get('incomePreviousYearToDate')?.value || null,
            };

            // Clear previous year fields
            this.form.patchValue(
                {
                    balancePreviousYearDate: null,
                    incomePreviousYearFromDate: null,
                    incomePreviousYearToDate: null,
                },
                { emitEvent: true },
            );

            // Drop the previous period's data source. The saved report derives noPreviousData, both
            // isPreviousDataTakenFromPreviousAnnualReport flags and both autofill report ids from these
            // four controls, so clearing them is what switches the report's autofill off as a whole.
            this.form.patchValue(
                {
                    autofillFromPreviousForBalance: false,
                    autofillReportForBalance: null,
                    autofillFromPreviousForIncome: false,
                    autofillReportForIncome: null,
                },
                { emitEvent: true },
            );

            // Make previous year fields not required
            this.form.get('balancePreviousYearDate')?.clearValidators();
            this.form.get('incomePreviousYearFromDate')?.clearValidators();
            this.form.get('incomePreviousYearToDate')?.clearValidators();
        } else {
            // Restore previous year dates or initialize them if none were stored
            this.restoreOrInitializePreviousYearDates();

            // Make previous year fields required if not in saved mode
            if (!this.isSavedMode) {
                this.form.get('balancePreviousYearDate')?.setValidators([Validators.required]);
                this.form.get('incomePreviousYearFromDate')?.setValidators([Validators.required]);
                this.form.get('incomePreviousYearToDate')?.setValidators([Validators.required]);
            }
        }

        this.form.get('balancePreviousYearDate')?.updateValueAndValidity();
        this.form.get('incomePreviousYearFromDate')?.updateValueAndValidity();
        this.form.get('incomePreviousYearToDate')?.updateValueAndValidity();
    }

    protected onAutofillReportForBalanceChange(autofillReportForBalanceId: string): void {
        this.autofillReportForBalanceSelected.emit(autofillReportForBalanceId);
    }
    protected onAutofillReportForIncomeChange(autofillReportForIncomeId: string): void {
        this.autofillReportForIncomeSelected.emit(autofillReportForIncomeId);
    }

    protected emitFormData(): void {
        const formValue = this.form.getRawValue();
        this.formDataChanged.emit(formValue);
    }

    protected restoreOrInitializePreviousYearDates(): void {
        if (this.storedPreviousYearDates) {
            // Restore the previously stored dates
            this.form.patchValue(
                {
                    balancePreviousYearDate: this.storedPreviousYearDates.balancePreviousYearDate,
                    incomePreviousYearFromDate: this.storedPreviousYearDates.incomePreviousYearFromDate,
                    incomePreviousYearToDate: this.storedPreviousYearDates.incomePreviousYearToDate,
                },
                { emitEvent: true },
            );
        } else if (!this.hasPreviousYearDates()) {
            // No stored dates - initialize them using the same logic as initial setup
            this.initializePreviousYearDates();
        }
    }

    // Tells whether the three previous period fields already carry a value
    protected hasPreviousYearDates(): boolean {
        return (
            this.form.get('balancePreviousYearDate')?.value != null &&
            this.form.get('incomePreviousYearFromDate')?.value != null &&
            this.form.get('incomePreviousYearToDate')?.value != null
        );
    }

    protected initializePreviousYearDates(): void {
        const balanceSheetDate = this.form.get('balanceSheetDate')?.value;
        const reportType = this.form.get('reportType')?.value;

        if (balanceSheetDate && reportType) {
            // Calculate dates using the same logic as initial setup
            const sheetDates = DatesCalculatorUtil.calculateFinancialStatementDates(
                balanceSheetDate.toISOString(),
                reportType.id as ReportType,
            );

            // Only update previous year dates (keep current year dates unchanged)
            this.form.patchValue(
                {
                    balancePreviousYearDate: sheetDates.balancePreviousYearDate,
                    incomePreviousYearFromDate: sheetDates.incomePreviousYearFromDate,
                    incomePreviousYearToDate: sheetDates.incomePreviousYearToDate,
                },
                { emitEvent: true },
            );
        }
    }

    protected clearStoredPreviousYearDates(): void {
        this.storedPreviousYearDates = null;
    }

    protected prepareSelectReportOptions(): void {
        this.balanceSheetReportOptions = this.previousAnnualReportOptions.map((report) => ({
            id: (report as any).id,
            name: `${this.dateLocalePipe.transform(new Date(report.generalInformation.balanceSheetDate)).toString()} (${this.dateLocalePipe.transform(new Date(report.generalInformation.incomeCurrentYearFromDate)).toString()} - ${this.dateLocalePipe.transform(new Date(report.generalInformation.incomeCurrentYearToDate)).toString()})`,
        }));

        this.incomeStatementReportOptions = this.previousAnnualReportOptions.map((report) => ({
            id: (report as any).id,
            name: `${this.dateLocalePipe
                .transform(new Date(report.generalInformation.incomeCurrentYearFromDate))
                .toString()} - ${this.dateLocalePipe
                    .transform(new Date(report.generalInformation.incomeCurrentYearToDate))
                    .toString()} `,
        }));
    }

    // #region Flyout Control Methods
    openBalanceSheetDateFlyout(buttonWrapper: HamButtonComponent): void {
        this.closeAllFlyouts();
        this.openedBalanceSheetDateFlyout = true;
        this.positionFlyout(buttonWrapper, '.balance-sheet-date-flyout');
    }

    closeBalanceSheetDateFlyout(): void {
        this.openedBalanceSheetDateFlyout = false;
    }

    openBalancePreviousDateFlyout(buttonWrapper: HamButtonComponent): void {
        this.closeAllFlyouts();
        this.openedBalancePreviousDateFlyout = true;
        this.positionFlyout(buttonWrapper, '.balance-previous-date-flyout');
    }

    closeBalancePreviousDateFlyout(): void {
        this.openedBalancePreviousDateFlyout = false;
    }

    openIncomeCurrentFromDateFlyout(buttonWrapper: HamButtonComponent): void {
        this.closeAllFlyouts();
        this.openedIncomeCurrentFromDateFlyout = true;
        this.positionFlyout(buttonWrapper, '.income-current-from-date-flyout');
    }

    closeIncomeCurrentFromDateFlyout(): void {
        this.openedIncomeCurrentFromDateFlyout = false;
    }

    openIncomeCurrentToDateFlyout(buttonWrapper: HamButtonComponent): void {
        this.closeAllFlyouts();
        this.openedIncomeCurrentToDateFlyout = true;
        this.positionFlyout(buttonWrapper, '.income-current-to-date-flyout');
    }

    closeIncomeCurrentToDateFlyout(): void {
        this.openedIncomeCurrentToDateFlyout = false;
    }

    openIncomePreviousFromDateFlyout(buttonWrapper: HamButtonComponent): void {
        this.closeAllFlyouts();
        this.openedIncomePreviousFromDateFlyout = true;
        this.positionFlyout(buttonWrapper, '.income-previous-from-date-flyout');
    }

    closeIncomePreviousFromDateFlyout(): void {
        this.openedIncomePreviousFromDateFlyout = false;
    }

    openIncomePreviousToDateFlyout(buttonWrapper: HamButtonComponent): void {
        this.closeAllFlyouts();
        this.openedIncomePreviousToDateFlyout = true;
        this.positionFlyout(buttonWrapper, '.income-previous-to-date-flyout');
    }

    closeIncomePreviousToDateFlyout(): void {
        this.openedIncomePreviousToDateFlyout = false;
    }

    private positionFlyout(buttonWrapper: HamButtonComponent, flyoutSelector: string): void {
        const buttonElement = buttonWrapper.nativeButton?.nativeElement;
        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const position = calculateFlyoutPosition(rect, undefined, 224, 446);

        this.flyoutPosition = {
            top: position.top,
            right: position.right,
            left: position.left,
            isAbove: position.isAbove,
        };

        // Reposition after render with actual dimensions
        this.flyoutPosition = repositionFlyoutAfterRender(flyoutSelector, rect);
    }

    private closeAllFlyouts(): void {
        this.openedBalanceSheetDateFlyout = false;
        this.openedBalancePreviousDateFlyout = false;
        this.openedIncomeCurrentFromDateFlyout = false;
        this.openedIncomeCurrentToDateFlyout = false;
        this.openedIncomePreviousFromDateFlyout = false;
        this.openedIncomePreviousToDateFlyout = false;
    }
    // #endregion

    // #region Date Update Methods
    updateBalanceSheetDate(date: Date): void {
        this.form.patchValue({ balanceSheetDate: date });
        this.closeBalanceSheetDateFlyout();
        this.openDialogForAutomaticDueDateChange = true;
    }

    updateBalancePreviousDate(date: Date): void {
        this.form.patchValue({ balancePreviousYearDate: date });
        this.closeBalancePreviousDateFlyout();
    }

    updateIncomeCurrentFromDate(date: Date): void {
        this.form.patchValue({ incomeCurrentYearFromDate: date });
        this.closeIncomeCurrentFromDateFlyout();
    }

    updateIncomeCurrentToDate(date: Date): void {
        this.form.patchValue({ incomeCurrentYearToDate: date });
        this.closeIncomeCurrentToDateFlyout();
    }

    updateIncomePreviousFromDate(date: Date): void {
        this.form.patchValue({ incomePreviousYearFromDate: date });
        this.closeIncomePreviousFromDateFlyout();
    }

    updateIncomePreviousToDate(date: Date): void {
        this.form.patchValue({ incomePreviousYearToDate: date });
        this.closeIncomePreviousToDateFlyout();
    }
    // #endregion

    // #region Select Today Methods
    selectTodayBalanceSheet(): void {
        const today = new Date();
        this.updateBalanceSheetDate(today);
    }

    selectTodayBalancePrevious(): void {
        const today = new Date();
        this.updateBalancePreviousDate(today);
    }

    selectTodayIncomeCurrentFrom(): void {
        const today = new Date();
        this.updateIncomeCurrentFromDate(today);
    }

    selectTodayIncomeCurrentTo(): void {
        const today = new Date();
        this.updateIncomeCurrentToDate(today);
    }

    selectTodayIncomePreviousFrom(): void {
        const today = new Date();
        this.updateIncomePreviousFromDate(today);
    }

    selectTodayIncomePreviousTo(): void {
        const today = new Date();
        this.updateIncomePreviousToDate(today);
    }
    // #endregion

    // #region Clear Date Methods
    clearBalanceSheetDate(): void {
        this.form.patchValue({ balanceSheetDate: null });
        this.closeBalanceSheetDateFlyout();
    }

    clearBalancePreviousDate(): void {
        this.form.patchValue({ balancePreviousYearDate: null });
        this.closeBalancePreviousDateFlyout();
    }

    clearIncomeCurrentFromDate(): void {
        this.form.patchValue({ incomeCurrentYearFromDate: null });
        this.closeIncomeCurrentFromDateFlyout();
    }

    clearIncomeCurrentToDate(): void {
        this.form.patchValue({ incomeCurrentYearToDate: null });
        this.closeIncomeCurrentToDateFlyout();
    }

    clearIncomePreviousFromDate(): void {
        this.form.patchValue({ incomePreviousYearFromDate: null });
        this.closeIncomePreviousFromDateFlyout();
    }

    clearIncomePreviousToDate(): void {
        this.form.patchValue({ incomePreviousYearToDate: null });
        this.closeIncomePreviousToDateFlyout();
    }
    // #endregion

    onEditDueDateConfirm(): void {

        const sheetDates = DatesCalculatorUtil.calculateFinancialStatementDates(
            this.form.get('balanceSheetDate')?.value,
            this.form.get('reportType')?.value.id as ReportType,
        );

        if (this.disablePreviousSettings) {
            this.form.patchValue({
                incomeCurrentYearFromDate: sheetDates.incomeCurrentYearFromDate,
                incomeCurrentYearToDate: sheetDates.incomeCurrentYearToDate,
            });

        } else {
            this.form.patchValue({
                balancePreviousYearDate: sheetDates.balancePreviousYearDate,
                incomeCurrentYearFromDate: sheetDates.incomeCurrentYearFromDate,
                incomeCurrentYearToDate: sheetDates.incomeCurrentYearToDate,
                incomePreviousYearFromDate: sheetDates.incomePreviousYearFromDate,
                incomePreviousYearToDate: sheetDates.incomePreviousYearToDate,
            });
        }

        this.openDialogForAutomaticDueDateChange = false;
    }

    // #region Form Control State Management
    protected updateFormControlStates(): void {
        this.updateCompanyControlState();
        this.updateAccountingBasisControlState();
        this.updateArTemplateControlState();
        this.updateLanguageControlState();
        this.updateReportTypeControlState();
        this.updateCurrencyControlState();
        this.updateDisplayCurrencyControlState();
        this.updateReportSelectionControlStates();
    }

    /**
     * The presentation currency is mandatory exactly when it is shown, i.e. on an FX report.
     * Validators are set here rather than in initializeForm() because `setupData` — which decides
     * it — is an @Input that is not yet bound when the form is built.
     */
    protected updateDisplayCurrencyControlState(): void {
        const control = this.form.get('displayCurrency');
        if (!control) return;

        if (this.isFxReport) {
            control.setValidators([Validators.required]);
        } else {
            control.clearValidators();
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    protected updateCompanyControlState(): void {
        this.setControlDisabledState('company', this.disableCompany);
    }

    protected updateAccountingBasisControlState(): void {
        this.setControlDisabledState('accountingBasis', this.disableAccountingBasis);
    }

    protected updateArTemplateControlState(): void {
        this.setControlDisabledState('arTemplate', this.disableArTemplate);

    }

    protected updateLanguageControlState(): void {
        this.setControlDisabledState('locale', this.disableLanguage);

    }

    protected updateReportTypeControlState(): void {
        this.setControlDisabledState('reportType', this.disableReportType);
    }

    protected updateCurrencyControlState(): void {
        this.setControlDisabledState('currency', this.disableCurrency);
    }

    protected updateReportSelectionControlStates(): void {
        const balanceControl = this.form.get('autofillReportForBalance');
        const incomeControl = this.form.get('autofillReportForIncome');

        if (balanceControl) {
            if (this.disablePreviousSettings) {
                balanceControl.disable({ emitEvent: false });
            } else {
                balanceControl.enable({ emitEvent: false });
            }
        }

        if (incomeControl) {
            if (this.disablePreviousSettings) {
                incomeControl.disable({ emitEvent: false });
            } else {
                incomeControl.enable({ emitEvent: false });
            }
        }
    }

    /**
    * Helper to enable/disable a form control by name.
    */
    protected setControlDisabledState(controlName: string, disabled: boolean): void {
        const control = this.form.get(controlName);
        if (!control) return;
        if (disabled) {
            control.disable({ emitEvent: false });
        } else {
            control.enable({ emitEvent: false });
        }
    }


}
